import { useMemo, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard, StyledSelect } from '@jumpstone/ui-react';
import type { MigrationJurisdiction } from '@jumpstone/core';
import type {
  DetectedSheet,
  MigrationSheetKind,
} from './migration_upload_logic';
import {
  defaultMapping,
  targetFieldsFor,
  type SheetMappings,
} from './migration_mapping_logic';

/** The mapped target for one file header, or '' when unset (safe at runtime). */
function mappingValue(
  mappings: SheetMappings,
  sourceName: string,
  header: string,
): string {
  if (!Object.hasOwn(mappings, sourceName)) return '';
  const sheetMap = mappings[sourceName];
  return Object.hasOwn(sheetMap, header) ? sheetMap[header] : '';
}

/**
 * Step 4 — Map: match each file column to a WagePro field. Pre-filled from the
 * pure `defaultMapping`; saved per entity by the shell on continue. Only the
 * classified sheets are shown; `''` means "don't import this column".
 */
export function MigrationMapStep({
  detectedSheets,
  jurisdiction,
  initialMappings,
  onBack,
  onNext,
}: {
  detectedSheets: DetectedSheet[];
  jurisdiction: MigrationJurisdiction;
  initialMappings: SheetMappings;
  onBack: () => void;
  onNext: (mappings: SheetMappings) => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const classified = useMemo(
    () =>
      detectedSheets.filter(
        (s): s is DetectedSheet & { kind: MigrationSheetKind } =>
          s.kind !== null,
      ),
    [detectedSheets],
  );

  const [mappings, setMappings] = useState<SheetMappings>(() => {
    const seed: SheetMappings = {};
    for (const sheet of classified) {
      seed[sheet.sourceName] =
        initialMappings[sheet.sourceName] ??
        defaultMapping(sheet.headers, sheet.kind, jurisdiction);
    }
    return seed;
  });

  function setField(source: string, header: string, target: string): void {
    setMappings((prev) => ({
      ...prev,
      [source]: { ...prev[source], [header]: target },
    }));
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="flex flex-col gap-5">
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('migration.map.description')}
        </p>

        {classified.map((sheet) => {
          const targetOptions = [
            { value: '', label: t('migration.map.ignore') },
            ...targetFieldsFor(sheet.kind, jurisdiction).map((field) => ({
              value: field,
              label: field,
            })),
          ];
          return (
            <div key={sheet.sourceName}>
              <h3 className="mb-2 text-sm font-semibold">{sheet.sourceName}</h3>
              <div className="flex flex-col gap-2">
                {sheet.headers.map((header) => (
                  <div key={header} className="flex items-center gap-3 text-sm">
                    <span className="min-w-0 flex-1 truncate">{header}</span>
                    <span className="text-[color:var(--ink-muted)]">→</span>
                    <div className="flex-1">
                      <StyledSelect
                        value={mappingValue(mappings, sheet.sourceName, header)}
                        onChange={(value) => {
                          setField(sheet.sourceName, header, value);
                        }}
                        options={targetOptions}
                        ariaLabel={t('migration.map.target_field')}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            {t('migration.button.back')}
          </Button>
          <Button
            onClick={() => {
              onNext(mappings);
            }}
          >
            {t('migration.button.next')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
}
